import { Link, Outlet } from 'react-router-dom';
import { Button, Logo } from '../components/ui';
import { BUILD_ID } from '../lib/build';
import { LanguageSwitcher } from '../components/LanguageSwitcher';
import { useT, type MessageKey } from '../i18n';
import { useSession } from '../lib/session';

/**
 * El pie legal.
 *
 * Las etiquetas son claves del catálogo, no texto: escritas a mano dejaban
 * «Terms · Privacy · Acceptable use · Contact» en inglés debajo de una página
 * en español, que es justo donde peor sienta —el pie legal es lo último que
 * lee quien está decidiendo si se fía—.
 */
const LEGAL: { to: string; label: MessageKey }[] = [
  { to: '/terms', label: 'nav.terms' },
  { to: '/privacy', label: 'nav.privacy' },
  { to: '/acceptable-use', label: 'nav.acceptableUse' },
  { to: '/contact', label: 'nav.contact' },
];

export function PublicLayout() {
  const { session } = useSession();
  const t = useT();

  return (
    <div className="flex min-h-dvh flex-col">
      <a href="#main" className="skip-link">
        {t('nav.skipToContent')}
      </a>

      <header className="sticky top-0 z-30 border-b border-line/40 bg-background/70 backdrop-blur-xl">
        <div className="mx-auto flex h-16 w-full max-w-6xl items-center justify-between gap-2 px-4 sm:gap-4">
          <Logo />

          {/*
            Cinco elementos —marca, idioma, precios, entrar, empezar— no caben
            en 390 px: sumaban 408 y el botón de «Empezar» se salía por la
            derecha, con lo que la página entera se podía arrastrar en
            horizontal. Es el síntoma que hace que un sitio se sienta roto en un
            teléfono aunque todo lo demás esté bien.

            El que se va en móvil es **«Precios»**, y no la marca ni el idioma.
            La marca es lo que dice dónde estás, y el selector de idioma es el
            único que existe antes de entrar —el de dentro de la aplicación no
            se alcanza sin cuenta—. «Precios», en cambio, está repetido dos
            veces más en la misma pantalla: el botón «Ver planes» del encabezado
            y el enlace del pie. Quitarlo de la barra no quita el camino.
          */}
          <nav className="flex min-w-0 items-center gap-0.5 sm:gap-1" aria-label={t('nav.main')}>
            <LanguageSwitcher compact />
            <Link
              to="/pricing"
              className="hidden rounded-xl px-3 py-2 text-sm text-muted transition hover:text-content sm:block"
            >
              {t('nav.pricing')}
            </Link>
            {session ? (
              <Button to="/dashboard" size="sm">
                {t('nav.dashboard')}
              </Button>
            ) : (
              <>
                <Link
                  to="/login"
                  className="rounded-xl px-2 py-2 text-sm text-muted transition hover:text-content sm:px-3"
                >
                  {t('nav.signIn')}
                </Link>
                <Button to="/register" size="sm">
                  {t('nav.getStarted')}
                </Button>
              </>
            )}
          </nav>
        </div>
      </header>

      <main id="main" className="flex-1">
        <Outlet />
      </main>

      <footer className="border-t border-line/40 py-10">
        <div className="mx-auto flex w-full max-w-6xl flex-col gap-6 px-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <Logo size="sm" />
            <p className="mt-2 text-sm text-muted">{t('common.tagline')}</p>
            <p className="mt-1 text-xs text-muted/60">build {BUILD_ID}</p>
          </div>
          <nav className="flex flex-wrap gap-x-5 gap-y-2" aria-label={t('nav.legal')}>
            {LEGAL.map((item) => (
              <Link key={item.to} to={item.to} className="text-sm text-muted hover:text-content">
                {t(item.label)}
              </Link>
            ))}
          </nav>
        </div>
      </footer>
    </div>
  );
}
